import { Clear, CopyAll } from "@mui/icons-material";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";

export default function MasterFileView({
  item,
  bulletPointToCopy,
  setBulletPointToCopy,
}: {
  item: { [key: string]: string };
  bulletPointToCopy: string | null;
  setBulletPointToCopy: (value: string | null) => void;
}) {
  // Track highlighted keys (for full value copies only)
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  // Track which key has the X button (for both full and partial selections)
  const [clearButtonKey, setClearButtonKey] = useState<string | null>(null);

  // Handle copying the entire value
  const handleCopyEntireValue = (key: string, value: string) => {
    setHighlightedKey(key); // Highlight for full value copies
    setClearButtonKey(key); // Show X button
    setBulletPointToCopy(value);
  };

  // Clear all selections
  const clearSelection = () => {
    setBulletPointToCopy(null);
    setHighlightedKey(null);
    setClearButtonKey(null);
  };

  // Handle text selection
  const handleMouseUp = (key: string) => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim() || "";
    if (selectedText !== "") {
      setBulletPointToCopy(selectedText);
      setClearButtonKey(key); // Only set the clear button key, NOT the highlight key
      setHighlightedKey(null); // Ensure no highlighting for partial selections
    }
  };

  // Reset selection when clicking elsewhere
  useEffect(() => {
    const handleDocumentClick = () => {
      window.getSelection()?.removeAllRanges();
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  // Clear component state on unmount
  useEffect(() => {
    return () => {
      setHighlightedKey(null);
    };
  }, []);

  // Reset when bulletPointToCopy is cleared externally
  useEffect(() => {
    if (bulletPointToCopy === null) {
      setHighlightedKey(null);
      setClearButtonKey(null);
    }
  }, [bulletPointToCopy]);

  return (
    <TableContainer component={Paper} sx={{ overflowX: "scroll" }}>
      <Table size="small">
        <TableBody>
          {Object.entries(item).map(([key, value]) => (
            <React.Fragment key={key}>
              <TableRow>
                <TableCell
                  sx={{
                    padding: "0.25rem 0",
                  }}
                >
                  <b>
                    {String(key)
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                  </b>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{
                    padding: "0.25rem 0",
                  }}
                >
                  <Box
                    sx={{
                      paddingRight: "0.5rem",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    {value ? (
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          userSelect: "text",
                          position: "relative",
                          // Only apply highlighting for full value copies
                          ...(highlightedKey === key && {
                            color: "primary.main",
                            textDecoration: "underline",
                          }),
                        }}
                        onMouseUp={() => handleMouseUp(key)}
                      >
                        {value}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                        }}
                      >
                        N/A
                      </Typography>
                    )}
                    <Box>
                      {/* Show the clear button where needed */}
                      {bulletPointToCopy && clearButtonKey === key && (
                        <Clear
                          onClick={clearSelection}
                          sx={{
                            fontSize: "1rem",
                            cursor: "pointer",
                            color: "black",
                            "&:hover": {
                              color: "black",
                            },
                          }}
                        />
                      )}

                      {/* Show the copy button when no text is selected/copied */}
                      {!bulletPointToCopy && value && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                          }}
                        >
                          <CopyAll
                            onClick={() => handleCopyEntireValue(key, value)}
                            sx={{
                              fontSize: "1rem",
                              cursor: "pointer",
                              color: "black",
                              opacity: 0.7,
                              "&:hover": {
                                color: "black",
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
